"use client"

import React, { FC, useRef, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { verifyReCaptcha } from "@/actions/reCaptchaActions"
import { zodResolver } from "@hookform/resolvers/zod"
import { Info } from "lucide-react"
import { useTranslations } from "next-intl"
import { renderToStaticMarkup } from "react-dom/server"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { SubmitHandler, useForm } from "react-hook-form"
import {
  MARK_BOLD,
  MARK_LINK,
  NODE_HEADING,
  NODE_PARAGRAPH,
  render,
  StoryblokRichtext,
} from "storyblok-rich-text-react-renderer"
import { z } from "zod"

import { sendContactEmail } from "@/lib/email/smtp-client"
import { resolveStoryblokStoryHref } from "@/lib/link-utils/storyblok-link-resolver"
import { isDevMode, isProdMode } from "@/lib/nodeHelper"
import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@/components/ui/components"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/components/tooltip"
import { renderRichTextChildrenOrBreak } from "@/components/ui/helpers/richtextRenderer"
import { cn } from "@/components/ui/helpers/utils"
import EmailTemplateContact from "@/components/email-templates/EmailTemplateContact"
import StoryblokAssetClient from "@/components/storyblok/common/StoryblokAssetClient"
import { AssetStoryblok, MultilinkStoryblok, RichtextStoryblok } from "@/components/storyblok/component-types"

import { buildValidationSchema, replacePlaceholder, sanitizeField } from "./UbReclamationForm.helpers"
import ReclamationFields from "./UbReclamationForm.ReclamationFields"
import ReturnFields from "./UbReclamationForm.ReturnFields"
import SubjectSelector from "./UbReclamationForm.Subject"

// settings for development purposes
const DEV_INSERT_DEFAULTS = isDevMode()
const DEV_SHOW_EMAIL_TEMPLATE = false
const DEV_SEND_EMAILS = isProdMode()
const DEV_SEND_ONLY_EXTERNAL_EMAIL = !isProdMode()
const DEV_EMAIL = process.env.NEXT_PUBLIC_DEV_EMAIL // env setting for inserting default value in email field

interface UbReclamationFormProps {
  fieldConfig?: {
    fieldConfigurationActive?: boolean
    subject_active?: boolean
    customer_number_active?: boolean
    customer_number_required?: boolean
    order_number_active?: boolean
    order_number_required?: boolean
    invoice_number_active?: boolean
    invoice_number_required?: boolean
    first_name_active?: boolean
    first_name_required?: boolean
    last_name_active?: boolean
    last_name_required?: boolean
    company_active?: boolean
    company_required?: boolean
    phone_active?: boolean
    phone_required?: boolean
    email_active?: boolean
    email_required?: boolean
    article_active?: boolean
    article_required?: boolean
    quantity_active?: boolean
    quantity_required?: boolean
    reason_for_complaint_active?: boolean
    damage_active?: boolean
    handling_active?: boolean
    handling_required?: boolean
    reason_for_return_active?: boolean
    reason_for_return_required?: boolean
    reaon_for_return_required?: boolean
    return_message_active?: boolean
    return_message_required?: boolean
  }
  httpRedirect: MultilinkStoryblok
  httpRedirectError: MultilinkStoryblok
  isoLocale: string
  sbLocale: string
  title?: RichtextStoryblok
  image?: AssetStoryblok
  description: RichtextStoryblok
  required?: string
  generalData?: string
  customerNumber?: string
  customerNumberInfo?: string
  orderNumber?: string
  orderNumberMissing?: string
  orderNumberInfo?: string
  invoiceNumber?: string
  invoiceNumberInfo?: string
  contactPartner?: string
  firstName?: string
  firstNameMissing?: string
  lastName?: string
  lastNameMissing?: string
  company?: string
  phone?: string
  phoneMissing?: string
  email?: string
  emailMissing?: string
  subject?: string
  subjectReclamation?: string
  subjectReturn?: string
  reclamation?: string
  article?: string
  articleMissing?: string
  quantity?: string
  quantityMissing?: string
  reasonForComplaint?: string
  reasonForComplaintReclamation?: string
  reasonForComplaintReturn?: string
  reasonForComplaintCancellation?: string
  reasonForComplaintOther?: string
  damage?: string
  damageYes?: string
  damageNo?: string
  reclamationMessage?: string
  handling?: string
  handlingInfo?: string
  handlingImprovement?: string
  handlingDiscount?: string
  handlingRefund?: string
  return?: string
  reasonForReturn?: string
  reasonForReturnIncorrect?: string
  reasonForReturnQuality?: string
  reasonForReturnOther?: string
  returnMessage?: string
  legalConsentNotification: RichtextStoryblok
  submit?: string
  captchaHint: RichtextStoryblok
  fromEmail: string
  toEmail: string
  emailSubject: string
  emailImage?: AssetStoryblok
  emailHtmlBody: RichtextStoryblok
  emailTextBody: string
  emailFooterLeft: RichtextStoryblok
  emailFooterRight: RichtextStoryblok
  emailFooterBottom: RichtextStoryblok
}

const renderedRichText = (richtext: StoryblokRichtext | unknown) => {
  return render(richtext, {
    nodeResolvers: {
      [NODE_HEADING]: (children, props) => {
        switch (props.level) {
          case 1:
            return <h1 className="mb-4 text-4xl font-bold">{children}</h1>
          case 2:
            return <h2 className="mb-3 text-3xl font-semibold">{children}</h2>
          case 3:
            return <h3 className="mb-2 text-2xl font-semibold">{children}</h3>
          case 4:
            return <h4 className="mb-2 text-xl font-medium">{children}</h4>
          case 5:
            return <h5 className="mb-1 text-lg font-medium">{children}</h5>
          case 6:
            return <h6 className="mb-1 text-base font-medium">{children}</h6>
          default:
            return <div>{children}</div> // In case level is not 1-6, return a default div
        }
      },
      [NODE_PARAGRAPH]: (children) => {
        return <>{renderRichTextChildrenOrBreak(children)}</>
      },
    },
    markResolvers: {
      [MARK_BOLD]: (children) => <span className={"font-bold"}>{children}</span>,
      [MARK_LINK]: (children, { linktype, href }) => {
        if (linktype === "story" && href) {
          return (
            <Link prefetch={false} className="text-primary" href={resolveStoryblokStoryHref(href)} target="_blank">
              {children}
            </Link>
          )
        }
        if (linktype === "url" && href) {
          return (
            <a className="text-primary" href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          )
        }
        return (
          <a className={"text-primary"} href={"#"}>
            {children}
          </a>
        )
      },
    },
  })
}

const UbReclamationFormClient: FC<UbReclamationFormProps> = (props: UbReclamationFormProps) => {
  const formRef = useRef<HTMLFormElement>(null)
  const router = useRouter()
  const t = useTranslations("storefront")
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [sending, setSending] = useState(false)

  const subjectReclamationValue = props.subjectReclamation ?? props.reclamation ?? "Reklamation"
  const subjectReturnValue = props.subjectReturn ?? props.return ?? "Retoure"

  let successRedirectLink = ""

  if (props.httpRedirect && props.httpRedirect?.linktype === "story" && props.httpRedirect.cached_url) {
    successRedirectLink = resolveStoryblokStoryHref(
      props.httpRedirect.cached_url,
      props.sbLocale,
      props.httpRedirect.story
    )
  }

  if (props.httpRedirect?.linktype === "url" && props.httpRedirect.url) {
    successRedirectLink = props.httpRedirect.url
  }

  let errorRedirectLink = ""

  if (props.httpRedirectError && props.httpRedirectError?.linktype === "story" && props.httpRedirectError.cached_url) {
    errorRedirectLink = resolveStoryblokStoryHref(
      props.httpRedirectError.cached_url,
      props.sbLocale,
      props.httpRedirectError.story
    )
  }

  if (props.httpRedirectError?.linktype === "url" && props.httpRedirectError.url) {
    errorRedirectLink = props.httpRedirectError.url
  }

  const fc = props.fieldConfig ?? {}
  const cfgActive = !!fc.fieldConfigurationActive
  const validationSchema = buildValidationSchema({
    t,
    fieldConfig: props.fieldConfig,
    subjectReclamationValue,
    orderNumberMissing: props.orderNumberMissing,
    articleMissing: props.articleMissing,
    quantityMissing: props.quantityMissing,
  }).superRefine((value, context) => {
    const isReclamation = value.ReclamationForm_Subject === subjectReclamationValue

    if (isReclamation) {
      const articleActive = !cfgActive || !!fc.article_active
      const articleReq = cfgActive ? !!fc.article_required : true
      if (articleActive && articleReq && !value.ReclamationForm_ReclamationArticle) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: props.articleMissing ?? t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReclamationArticle"],
        })
      }

      const qtyActive = !cfgActive || !!fc.quantity_active
      const qtyReq = cfgActive ? !!fc.quantity_required : true
      if (qtyActive && qtyReq && !value.ReclamationForm_ReclamationQuantity) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: props.quantityMissing ?? t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReclamationQuantity"],
        })
      }

      const reasonActive = !cfgActive || !!fc.reason_for_complaint_active
      const reasonReq = cfgActive ? !!(fc as any).reason_for_complaint_required : true
      if (reasonActive && reasonReq && !value.ReclamationForm_ReclamationReason) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReclamationReason"],
        })
      }

      const damageActive = !cfgActive || !!fc.damage_active
      const damageReq = cfgActive ? !!(fc as any).damage_required : true
      if (damageActive && damageReq && !value.ReclamationForm_ReclamationDamage) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReclamationDamage"],
        })
      }

      const msgActive = !cfgActive || !!(fc as any).reclamation_message_active
      const msgReq = cfgActive ? !!(fc as any).reclamation_message_required : true
      if (msgActive && msgReq && !value.ReclamationForm_ReclamationMessage) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReclamationMessage"],
        })
      }

      const handlingActive = !cfgActive || !!fc.handling_active
      const handlingReq = cfgActive ? !!fc.handling_required : true
      if (handlingActive && handlingReq && !value.ReclamationForm_ReclamationHandling) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReclamationHandling"],
        })
      }
    } else {
      const articleActive = !cfgActive || !!fc.article_active
      const articleReq = cfgActive ? !!fc.article_required : true
      if (articleActive && articleReq && !value.ReclamationForm_ReturnArticle) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: props.articleMissing ?? t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReturnArticle"],
        })
      }
      const qtyActive = !cfgActive || !!fc.quantity_active
      const qtyReq = cfgActive ? !!fc.quantity_required : true
      if (qtyActive && qtyReq && !value.ReclamationForm_ReturnQuantity) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: props.quantityMissing ?? t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReturnQuantity"],
        })
      }
      const rrActive = !cfgActive || !!fc.reason_for_return_active
      const rrReq = cfgActive ? !!(fc.reason_for_return_required ?? fc.reaon_for_return_required) : true
      if (rrActive && rrReq && !value.ReclamationForm_ReturnReason) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReturnReason"],
        })
      }

      const rmsgActive = !cfgActive || !!fc.return_message_active
      const rmsgReq = cfgActive ? !!fc.return_message_required : false
      if (rmsgActive && rmsgReq && !value.ReclamationForm_ReturnMessage) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("forms.contact_us_generic_form.error.subject"),
          path: ["ReclamationForm_ReturnMessage"],
        })
      }
    }
  })

  type ValidationSchemaType = z.infer<typeof validationSchema>

  const form = useForm<ValidationSchemaType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      ReclamationForm_CustomerId: DEV_INSERT_DEFAULTS ? "112233445566" : "",
      ReclamationForm_OrderNumber: DEV_INSERT_DEFAULTS ? "" : "",
      ReclamationForm_InvoiceNumber: DEV_INSERT_DEFAULTS ? "" : "",
      ReclamationForm_FirstName: DEV_INSERT_DEFAULTS ? "John" : "",
      ReclamationForm_LastName: DEV_INSERT_DEFAULTS ? "Doe" : "",
      ReclamationForm_CompanyName: DEV_INSERT_DEFAULTS ? "ACME Company" : "",
      ReclamationForm_Phone: DEV_INSERT_DEFAULTS ? "123456" : "",
      ReclamationForm_Email: DEV_INSERT_DEFAULTS ? DEV_EMAIL : "",
      ReclamationForm_Subject: subjectReclamationValue,
      ReclamationForm_ReclamationArticle: "",
      ReclamationForm_ReclamationQuantity: "",
      ReclamationForm_ReclamationReason: "",
      ReclamationForm_ReclamationDamage: "",
      ReclamationForm_ReclamationMessage: "",
      ReclamationForm_ReclamationHandling: "",
      ReclamationForm_ReturnArticle: "",
      ReclamationForm_ReturnQuantity: "",
      ReclamationForm_ReturnReason: "",
      ReclamationForm_ReturnMessage: "",
    },
  })

  const getEmailSubject = (value: ValidationSchemaType) => {
    return replacePlaceholder(props.emailSubject, value, subjectReclamationValue)
  }

  const getEmailTextBody = (value: ValidationSchemaType) => {
    return replacePlaceholder(props.emailTextBody, value, subjectReclamationValue)
  }

  const getEmailHtmlBody = (value: ValidationSchemaType) => {
    const richtextReplaced = replacePlaceholder(JSON.stringify(props.emailHtmlBody), value, subjectReclamationValue)
    const body = JSON.parse(richtextReplaced)

    return renderToStaticMarkup(
      <EmailTemplateContact
        sbLocale={props.sbLocale}
        body={body}
        image={props.emailImage}
        footerLeft={props.emailFooterLeft}
        footerRight={props.emailFooterRight}
        footerBottom={props.emailFooterBottom}
      />
    )
  }

  const sendEmails = (value: ValidationSchemaType) => {
    // console.log("sendEmails")
    const emailTextBody = getEmailTextBody(value)
    // console.log("emailTextBody: "+emailTextBody)

    const emailHtmlBody = getEmailHtmlBody(value)
    // console.log("emailHtmlBody: "+emailHtmlBody)

    const emailSubject = getEmailSubject(value)
    // console.log("emailSubject: "+emailSubject)

    const externalEmail = () =>
      sendContactEmail({
        from: props.fromEmail,
        to: value.ReclamationForm_Email ?? "",
        subject: emailSubject,
        text: emailTextBody,
        html: emailHtmlBody,
      })

    const internalEmail = () =>
      sendContactEmail({
        from: props.fromEmail,
        to: props.toEmail,
        subject: emailSubject,
        text: emailTextBody,
        html: emailHtmlBody,
      })

    if (DEV_SEND_EMAILS) {
      Promise.all([externalEmail(), internalEmail()])
        .then(() => redirect(true))
        .catch((error) => {
          console.log(`UbReclamationFormClient: error sending emails: ${error}`)
          redirect(false)
        })
    } else if (DEV_SEND_ONLY_EXTERNAL_EMAIL) {
      externalEmail()
        .then(() => redirect(true))
        .catch((error) => {
          console.log(`UbReclamationFormClient: error sending external email: ${error}`)
          redirect(false)
        })
    } else {
      console.log("UbReclamationFormClient: email delivery is disabled by development switch DEV_SEND_EMAILS")
      redirect(true)
    }
  }

  const redirect = (succeeded: boolean) => {
    try {
      succeeded ? router.push(successRedirectLink) : router.push(errorRedirectLink)
    } catch (error) {
      console.log(`UbReclamationFormClient: error on redirecting: ${error}`)
    }
  }

  const onSubmit: SubmitHandler<ValidationSchemaType> = (data: ValidationSchemaType, event) => {
    event && event.preventDefault()
    const action = "submit_contact_form" // be careful - seems not to accept spaces etc.
    setSending(true)

    if (executeRecaptcha) {
      executeRecaptcha(action)
        .then((token) => {
          verifyReCaptcha(token, action, props.subject ?? "")
            .then((result) => {
              result ? sendEmails(data) : redirect(false)
            })
            .catch((error) => {
              console.log(`UbReclamationFormClient: error executing verifyReCaptcha: ${error.message}`)
              redirect(false)
            })
        })
        .catch((error) => {
          console.log(`UbReclamationFormClient: error executing recaptcha: ${error.message}`)
          redirect(false)
        })
    } else {
      console.log("UbReclamationFormClient: execute recaptcha not available yet")
      redirect(false)
    }
  }

  const styleWithoutOutline =
    "focus:ring-offset-0 focus-visible:ring-offset-0 focus:ring-0 focus:outline-0 focus-visible:outline-0 focus-visible:ring-0"
  const focusBorders = "focus:border-primary focus:border-2 focus-visible:border-primary focus-visible:border-2"
  const textFieldStyleBase = styleWithoutOutline + " " + focusBorders + " pl-4 w-full lg:max-w-[360px]"
  const textFieldStyle = textFieldStyleBase + " pt-1.5 rounded-lg"
  const textFieldStyleMultiline = textFieldStyleBase + " pt-3 rounded-lg"
  const radioButtonStyle = styleWithoutOutline + " " + focusBorders + " size-5"
  const checkboxStyle = styleWithoutOutline + " " + focusBorders + " mr-4 size-5"
  const formItemClass = "grid gap-2 lg:grid-cols-[minmax(280px,300px)_minmax(0,1fr)] lg:items-center"
  const formLabelClass = "text-base leading-snug lg:pr-2"
  const formLabelWithIconClass = "flex flex-wrap items-center gap-x-2 text-base leading-snug lg:pr-2"

  const selectedSubject = form.watch("ReclamationForm_Subject")
  const isReclamationSubject = selectedSubject === subjectReclamationValue
  const show = (key: any) => (!cfgActive ? true : !!(fc as any)[key])
  // required flags per field (default to original behavior when configuration inactive)
  const reqCustomerNumber = cfgActive ? !!fc.customer_number_required : false
  const reqOrderNumber = cfgActive ? !!fc.order_number_required : true
  const reqInvoiceNumber = cfgActive ? !!fc.invoice_number_required : false
  const reqFirstName = cfgActive ? !!fc.first_name_required : true
  const reqLastName = cfgActive ? !!fc.last_name_required : true
  const reqCompany = cfgActive ? !!fc.company_required : false
  const reqPhone = cfgActive ? !!fc.phone_required : true
  const reqEmail = cfgActive ? !!fc.email_required : true

  const reqReclArticle = cfgActive ? !!fc.article_required : true
  const reqReclQuantity = cfgActive ? !!fc.quantity_required : true
  const reqReclReason = cfgActive ? !!(fc as any).reason_for_complaint_required : true
  const reqReclDamage = cfgActive ? !!(fc as any).damage_required : true
  const reqReclMessage = cfgActive ? !!(fc as any).reclamation_message_required : true
  const reqReclHandling = cfgActive ? !!fc.handling_required : true

  const reqRetArticle = cfgActive ? !!fc.article_required : true
  const reqRetQuantity = cfgActive ? !!fc.quantity_required : true
  const reqRetReason = cfgActive ? !!(fc.reason_for_return_required ?? fc.reaon_for_return_required) : true
  const reqRetMessage = cfgActive ? !!fc.return_message_required : false
  const reclamationLabel = props.subjectReclamation ?? "Reklamation"
  const returnLabel = props.subjectReturn ?? "Retoure"
  const reclamationReasonOptions = [
    props.reasonForComplaintReclamation,
    props.reasonForComplaintReturn,
    props.reasonForComplaintCancellation,
    props.reasonForComplaintOther,
  ].filter((value): value is string => Boolean(value))
  const returnReasonOptions = [
    props.reasonForReturnIncorrect,
    props.reasonForReturnQuality,
    props.reasonForReturnOther,
  ].filter((value): value is string => Boolean(value))
  const handlingOptions = [props.handlingImprovement, props.handlingDiscount, props.handlingRefund].filter(
    (value): value is string => Boolean(value)
  )

  return (
    <>
      <div className="flex flex-col-reverse lg:flex-row" style={{ overflowAnchor: "none" }}>
        <div
          className={cn("bg-muted px-10 pb-6 pt-10 lg:w-[57%] xl:px-20", {
            "pointer-events-none opacity-30 cursor-wait": sending,
          })}
        >
          <div className="mb-12">{renderedRichText(props.description)}</div>
          <p className={"text-sm text-muted-foreground"}>
            <span>{props.required ?? "Mit einem * markierte Felder sind Pflichtfelder."}</span>
          </p>
          <Form {...form}>
            <form className="flex flex-col" ref={formRef} name="ReclamationForm" onSubmit={form.handleSubmit(onSubmit)}>
              <fieldset className="mb-4 grid grid-cols-1 gap-4">
                <input type="hidden" name="ReclamationForm_Topic" value={props.subject ?? ""} />

                <input type="hidden" name="ReclamationForm_Subject" value={form.watch("ReclamationForm_Subject")} />

                {/* Allgemeine Daten */}
                <div className="mt-6">
                  <p className="mb-2 text-lg font-semibold">{props.generalData ?? "Allgemeine Daten"}</p>
                </div>

                {show("customer_number_active") && (
                  <FormField
                    control={form.control}
                    name="ReclamationForm_CustomerId"
                    render={({ field }) => (
                      <FormItem className={formItemClass}>
                        <label className={formLabelWithIconClass}>
                          <span>
                            {props.customerNumber ?? t("forms.contact_us_generic_form.placeholder.customer_id")}
                          </span>
                          {props.customerNumberInfo && (
                            <TooltipProvider delayDuration={50} skipDelayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="w-fit cursor-pointer">
                                    <Info className="size-4 text-gray-400" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm" sideOffset={4}>
                                  {props.customerNumberInfo}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {reqCustomerNumber && <span className="text-sm text-primary">*</span>}
                        </label>
                        <FormControl>
                          <Input
                            className={textFieldStyle}
                            {...field}
                            placeholder={
                              props.customerNumber ?? t("forms.contact_us_generic_form.placeholder.customer_id")
                            }
                          />
                        </FormControl>
                        <FormMessage className="lg:col-start-2" />
                      </FormItem>
                    )}
                  />
                )}

                {show("order_number_active") && (
                  <FormField
                    control={form.control}
                    name="ReclamationForm_OrderNumber"
                    render={({ field }) => (
                      <FormItem className={formItemClass}>
                        <label className={formLabelWithIconClass}>
                          <span>
                            {props.orderNumber ?? t("forms.contact_us_generic_form.placeholder.order_number")}
                          </span>
                          {props.orderNumberInfo && (
                            <TooltipProvider delayDuration={50} skipDelayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="w-fit cursor-pointer">
                                    <Info className="size-4 text-gray-400" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm" sideOffset={4}>
                                  {props.orderNumberInfo}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {reqOrderNumber && <span className="text-sm text-primary">*</span>}
                        </label>
                        <FormControl>
                          <Input
                            className={textFieldStyle}
                            {...field}
                            placeholder={
                              props.orderNumber ?? t("forms.contact_us_generic_form.placeholder.order_number")
                            }
                          />
                        </FormControl>
                        <FormMessage className="lg:col-start-2" />
                      </FormItem>
                    )}
                  />
                )}

                {show("invoice_number_active") && (
                  <FormField
                    control={form.control}
                    name="ReclamationForm_InvoiceNumber"
                    render={({ field }) => (
                      <FormItem className={formItemClass}>
                        <label className={formLabelWithIconClass}>
                          <span>
                            {props.invoiceNumber ?? t("forms.contact_us_generic_form.placeholder.invoice_number")}
                          </span>
                          {props.invoiceNumberInfo && (
                            <TooltipProvider delayDuration={50} skipDelayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="w-fit cursor-pointer">
                                    <Info className="size-4 text-gray-400" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm" sideOffset={4}>
                                  {props.invoiceNumberInfo}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </label>
                        <FormControl>
                          <Input
                            className={textFieldStyle}
                            {...field}
                            placeholder={
                              props.invoiceNumber ?? t("forms.contact_us_generic_form.placeholder.invoice_number")
                            }
                          />
                        </FormControl>
                        <FormMessage className="lg:col-start-2" />
                      </FormItem>
                    )}
                  />
                )}

                {/* Ansprechpartner */}
                <div className="mt-6">
                  <p className="mb-2 text-lg font-semibold">
                    {props.contactPartner ?? "Ansprechpartner für Rekla-/Retourenabwicklung"}
                  </p>
                </div>

                {show("first_name_active") && (
                  <FormField
                    control={form.control}
                    name="ReclamationForm_FirstName"
                    render={({ field }) => (
                      <FormItem className={formItemClass}>
                        <label className={formLabelClass}>
                          {props.firstName ?? t("forms.contact_us_generic_form.placeholder.first_name")}
                          {reqFirstName && <span className="ml-1 text-sm text-primary">*</span>}
                        </label>
                        <FormControl>
                          <Input
                            className={textFieldStyle}
                            {...field}
                            placeholder={props.firstName ?? t("forms.contact_us_generic_form.placeholder.first_name")}
                          />
                        </FormControl>
                        <FormMessage className="lg:col-start-2" />
                      </FormItem>
                    )}
                  />
                )}

                {show("last_name_active") && (
                  <FormField
                    control={form.control}
                    name="ReclamationForm_LastName"
                    render={({ field }) => (
                      <FormItem className={formItemClass}>
                        <label className={formLabelClass}>
                          {props.lastName ?? t("forms.contact_us_generic_form.placeholder.last_name")}
                          {reqLastName && <span className="ml-1 text-sm text-primary">*</span>}
                        </label>
                        <FormControl>
                          <Input
                            className={textFieldStyle}
                            {...field}
                            placeholder={props.lastName ?? t("forms.contact_us_generic_form.placeholder.last_name")}
                          />
                        </FormControl>
                        <FormMessage className="lg:col-start-2" />
                      </FormItem>
                    )}
                  />
                )}

                {show("company_active") && (
                  <FormField
                    control={form.control}
                    name="ReclamationForm_CompanyName"
                    render={({ field }) => (
                      <FormItem className={formItemClass}>
                        <label className={formLabelClass}>
                          {props.company ?? t("forms.contact_us_generic_form.placeholder.company_name")}
                          {reqCompany && <span className="ml-1 text-sm text-primary">*</span>}
                        </label>
                        <FormControl>
                          <Input
                            className={textFieldStyle}
                            {...field}
                            placeholder={props.company ?? t("forms.contact_us_generic_form.placeholder.company_name")}
                          />
                        </FormControl>
                        <FormMessage className="lg:col-start-2" />
                      </FormItem>
                    )}
                  />
                )}

                {show("phone_active") && (
                  <FormField
                    control={form.control}
                    name="ReclamationForm_Phone"
                    render={({ field }) => (
                      <FormItem className={formItemClass}>
                        <label className={formLabelClass}>
                          {props.phone ?? t("forms.contact_us_generic_form.placeholder.phone")}
                          {reqPhone && <span className="ml-1 text-sm text-primary">*</span>}
                        </label>
                        <FormControl>
                          <Input
                            className={textFieldStyle}
                            type={"tel"}
                            {...field}
                            placeholder={props.phone ?? t("forms.contact_us_generic_form.placeholder.phone")}
                          />
                        </FormControl>
                        <FormMessage className="lg:col-start-2" />
                      </FormItem>
                    )}
                  />
                )}

                {show("email_active") && (
                  <FormField
                    control={form.control}
                    name="ReclamationForm_Email"
                    render={({ field }) => (
                      <FormItem className={formItemClass}>
                        <label className={formLabelClass}>
                          {props.email ?? t("forms.contact_us_generic_form.placeholder.email")}
                          {reqEmail && <span className="ml-1 text-sm text-primary">*</span>}
                        </label>
                        <FormControl>
                          <Input
                            className={textFieldStyle}
                            type={"email"}
                            {...field}
                            placeholder={props.email ?? t("forms.contact_us_generic_form.placeholder.email")}
                          />
                        </FormControl>
                        <FormMessage className="lg:col-start-2" />
                      </FormItem>
                    )}
                  />
                )}

                <SubjectSelector
                  showSubject={show("subject_active")}
                  form={form}
                  formItemClass={formItemClass}
                  formLabelClass={formLabelClass}
                  radioButtonStyle={radioButtonStyle}
                  subjectLabel={props.subject ?? "Betreff"}
                  subjectReclamationValue={subjectReclamationValue}
                  subjectReclamationLabel={reclamationLabel}
                  subjectReturnValue={subjectReturnValue}
                  subjectReturnLabel={returnLabel}
                />

                {isReclamationSubject ? (
                  <ReclamationFields
                    form={form}
                    t={t}
                    show={show as any}
                    reqReclArticle={reqReclArticle}
                    reqReclQuantity={reqReclQuantity}
                    reqReclReason={reqReclReason}
                    reqReclDamage={reqReclDamage}
                    reqReclMessage={reqReclMessage}
                    reqReclHandling={reqReclHandling}
                    reclamationLabel={reclamationLabel}
                    article={props.article}
                    quantity={props.quantity}
                    reasonForComplaint={props.reasonForComplaint}
                    damage={props.damage}
                    damageYes={props.damageYes}
                    damageNo={props.damageNo}
                    reclamationMessage={props.reclamationMessage}
                    handling={props.handling}
                    handlingInfo={props.handlingInfo}
                    reclamationReasonOptions={reclamationReasonOptions}
                    handlingOptions={handlingOptions}
                    formItemClass={formItemClass}
                    formLabelClass={formLabelClass}
                    formLabelWithIconClass={formLabelWithIconClass}
                    textFieldStyle={textFieldStyle}
                    textFieldStyleMultiline={textFieldStyleMultiline}
                    radioButtonStyle={radioButtonStyle}
                  />
                ) : (
                  <ReturnFields
                    form={form}
                    t={t}
                    show={show as any}
                    reqRetArticle={reqRetArticle}
                    reqRetQuantity={reqRetQuantity}
                    reqRetReason={reqRetReason}
                    reqRetMessage={reqRetMessage}
                    returnLabel={returnLabel}
                    article={props.article}
                    quantity={props.quantity}
                    reasonForReturn={props.reasonForReturn}
                    returnMessage={props.returnMessage}
                    returnReasonOptions={returnReasonOptions}
                    formItemClass={formItemClass}
                    formLabelClass={formLabelClass}
                    textFieldStyle={textFieldStyle}
                    textFieldStyleMultiline={textFieldStyleMultiline}
                  />
                )}

                <FormField
                  control={form.control}
                  name="ReclamationForm_AcceptTerms"
                  render={({ field }) => (
                    <FormItem className="mb-6">
                      <FormControl>
                        <div className="flex flex-row items-center">
                          <Checkbox
                            id="ReclamationForm_AcceptTerms"
                            className={checkboxStyle}
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                          <label className="text-sm" htmlFor="ReclamationForm_AcceptTerms">
                            {renderedRichText(props.legalConsentNotification)}
                            <span className="text-sm text-primary">*</span>
                          </label>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </fieldset>

              <Button type="submit" size="lg" className="mb-6 self-center" disabled={sending}>
                {props.submit ?? t("forms.contact_us_generic_form.send")}
              </Button>
              <p className={"mb-6 text-sm"}>{renderedRichText(props.captchaHint)}</p>
            </form>
          </Form>
        </div>
        <div className="flex flex-row bg-primary p-10 lg:max-w-[43%] lg:flex-col">
          {props.title && <div className="mb-8 w-full md:w-3/5 lg:w-full">{renderedRichText(props.title)}</div>}
          {props.image && (
            <div className="hidden md:block md:max-w-[40%] lg:max-w-none">
              <StoryblokAssetClient className=" object-scale-down" asset={props.image} loading="eager" />
            </div>
          )}
        </div>
      </div>
      {/* only used for development */}
      {DEV_SHOW_EMAIL_TEMPLATE && (
        <div
          dangerouslySetInnerHTML={{
            __html: getEmailHtmlBody(form.getValues()),
          }}
        />
      )}
    </>
  )
}

export default UbReclamationFormClient
